interface JwtToken {
  accessToken: string;
  refreshToken: string;
}

export enum AuthSyncEventType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  TOKEN_REFRESH = 'TOKEN_REFRESH',
}

type AuthSyncEvent =
  | { type: AuthSyncEventType.LOGIN; jwt: JwtToken }
  | { type: AuthSyncEventType.LOGOUT }
  | { type: AuthSyncEventType.TOKEN_REFRESH; jwt: JwtToken };

class AuthSyncService {
  private channel: BroadcastChannel | null = null;
  private listeners: Set<(event: AuthSyncEvent) => void> = new Set();

  constructor() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        this.channel = new BroadcastChannel('auth-sync');
        this.setupListener();
      } catch {
        this.channel = null;
      }
    }
  }

  private setupListener() {
    if (!this.channel) return;

    this.channel.onmessage = (event: MessageEvent<AuthSyncEvent>) => {
      this.listeners.forEach((listener) => listener(event.data));
    };
  }

  broadcastLogin(jwt: JwtToken) {
    if (!this.channel) return;

    const event: AuthSyncEvent = { type: AuthSyncEventType.LOGIN, jwt };
    this.channel.postMessage(event);
  }

  broadcastLogout() {
    if (!this.channel) return;

    const event: AuthSyncEvent = { type: AuthSyncEventType.LOGOUT };
    this.channel.postMessage(event);
  }

  broadcastTokenRefresh(jwt: JwtToken) {
    if (!this.channel) return;

    const event: AuthSyncEvent = { type: AuthSyncEventType.TOKEN_REFRESH, jwt };
    this.channel.postMessage(event);
  }

  subscribe(callback: (event: AuthSyncEvent) => void) {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

export const authSyncService = new AuthSyncService();
